(() => {
  'use strict';

  const mobile = window.matchMedia('(max-width: 749px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const tracks = Array.from(document.querySelectorAll('.abm-product-grid, .abm-research-grid, .abm-service-stack'));
  if (!tracks.length) return;

  function initTrack(track, initiallyNear = true) {
    if (!track || track.dataset.abmiumCarouselReady === 'true') return null;

    const slides = Array.from(track.children);
    if (slides.length < 2) return null;

    track.dataset.abmiumCarouselReady = 'true';
    const productDeck = track.classList.contains('abm-product-grid');
    const serviceDeck = track.classList.contains('abm-service-stack');
    let intervalId = 0;
    let resumeId = 0;
    let scrollFrame = 0;
    let resizeFrame = 0;
    let paused = false;
    let nearViewport = initiallyNear;
    let pageIndexes = [];
    let dots = [];

    const eligible = () => !serviceDeck && !reduced.matches && (productDeck || mobile.matches);
    const canAutoplay = () => eligible() && nearViewport && !document.hidden;
    const pageSize = () => {
      if (!productDeck || mobile.matches) return 1;
      return window.innerWidth <= 1080 ? 2 : 3;
    };
    const slideLeft = (slide) => slide.offsetLeft - track.offsetLeft;

    const makePages = () => {
      const size = pageSize();
      const next = [];
      for (let index = 0; index < slides.length; index += size) next.push(index);
      return next;
    };

    const currentPage = () => {
      let closest = 0;
      let distance = Infinity;
      pageIndexes.forEach((slideIndex, pageIndex) => {
        const next = Math.abs(slideLeft(slides[slideIndex]) - track.scrollLeft);
        if (next < distance) {
          distance = next;
          closest = pageIndex;
        }
      });
      return closest;
    };

    const pagination = document.createElement('div');
    pagination.className = 'abm-carousel-pagination';
    pagination.setAttribute('role', 'group');
    pagination.setAttribute('aria-label', productDeck ? 'Featured product categories pagination' : serviceDeck ? 'Scientific services pagination' : 'Research areas pagination');
    track.insertAdjacentElement('afterend', pagination);

    const setActive = (page) => {
      dots.forEach((dot, index) => {
        const active = index === page;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const goTo = (page) => {
      const slideIndex = pageIndexes[page];
      if (slideIndex === undefined) return;
      track.scrollTo({ left: slideLeft(slides[slideIndex]), behavior: reduced.matches ? 'auto' : 'smooth' });
      setActive(page);
    };

    const pauseForInteraction = () => {
      if (!eligible()) return;
      paused = true;
      window.clearTimeout(resumeId);
      resumeId = window.setTimeout(() => { paused = false; }, 8000);
    };

    const rebuildPagination = () => {
      const active = pageIndexes.length ? currentPage() : 0;
      pageIndexes = makePages();
      pagination.replaceChildren();
      dots = pageIndexes.map((slideIndex, page) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'abm-carousel-dot';
        dot.setAttribute('aria-label', `Show group ${page + 1} of ${pageIndexes.length}`);
        dot.addEventListener('click', () => {
          pauseForInteraction();
          goTo(page);
        });
        pagination.appendChild(dot);
        return dot;
      });
      setActive(Math.min(active, Math.max(0, dots.length - 1)));
    };

    const advance = () => {
      if (!canAutoplay() || paused || pageIndexes.length < 2) return;
      goTo((currentPage() + 1) % pageIndexes.length);
    };

    const start = () => {
      window.clearInterval(intervalId);
      intervalId = 0;
      if (canAutoplay() && pageIndexes.length > 1) intervalId = window.setInterval(advance, 4200);
    };

    track.addEventListener('scroll', () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = 0;
        setActive(currentPage());
      });
    }, { passive: true });

    ['pointerdown', 'touchstart', 'wheel'].forEach((eventName) => {
      track.addEventListener(eventName, pauseForInteraction, { passive: true });
    });
    track.addEventListener('focusin', pauseForInteraction);

    const refresh = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        rebuildPagination();
        start();
      });
    };

    window.addEventListener('resize', refresh, { passive: true });
    if (mobile.addEventListener) mobile.addEventListener('change', refresh);
    else mobile.addListener(refresh);
    if (reduced.addEventListener) reduced.addEventListener('change', refresh);
    else reduced.addListener(refresh);
    document.addEventListener('visibilitychange', start);

    rebuildPagination();
    start();

    return {
      setNearViewport(value) {
        if (nearViewport === value) return;
        nearViewport = value;
        start();
      }
    };
  }

  tracks.forEach((track) => {
    if (!('IntersectionObserver' in window) || Boolean(window.Shopify && window.Shopify.designMode)) {
      initTrack(track, true);
      return;
    }

    let controller = null;
    const observer = new IntersectionObserver((entries) => {
      const near = entries.some((entry) => entry.isIntersecting);
      if (near && !controller) controller = initTrack(track, true);
      if (controller) controller.setNearViewport(near);
    }, { rootMargin: '240px 0px' });

    observer.observe(track);

    const activate = () => {
      if (!controller) controller = initTrack(track, true);
    };
    track.addEventListener('pointerdown', activate, { once: true, passive: true });
    track.addEventListener('touchstart', activate, { once: true, passive: true });
    track.addEventListener('focusin', activate, { once: true });
  });
})();

